import React, { useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { ContentGroup } from '../../api/types/contentGroup';
import { getAllContentGroups } from '../../api/contentGroup';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import ProjectTableHeader from '../../components/ProjectTableHeader';
import ProjectTable from '../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../components/ProjectTable/constants';
import useFormTextState from '../../hooks/useFormTextState';
import { AppSettingsContext } from '../../providers/AppSettingsProvider/context';
import { useNavigate } from 'react-router-dom';
import { getDisplayNames } from '../../api/utils';
import ProjectSearchDropdown from '../../components/ProjectSearchDropdown';
import { CONTENT_GROUP_TYPES, ContentGroupType } from '../../api/types/others';
import useCustomizedDropdown from '../../hooks/useCustomizedDropdown';
import ProjectPicker from '../../components/ProjectPicker';

const ContentGroups = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, pillarExpertises } = useContext(AppSettingsContext);
  const navigate = useNavigate();

  const [data, setData] = useState<ContentGroup[]>([]);
  const [contentGroupType, setContentGroupType] = useState<ContentGroupType>();

  const searchProps = useFormTextState();
  const { props: pillarTypeProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertiseProps } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });
  const { props: categoryProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });
  const { props: contentGroupTypeProps, setProps: setContentGroupTypeProps } = useCustomizedDropdown({
    possibleValues: CONTENT_GROUP_TYPES,
    singleSelect: true,
    customType: 'contentGroupType',
  });

  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllContentGroups({
      category: categoryProps.selectedItems?.[0]?.value,
      pillarType: pillarTypeProps.selectedItems?.[0]?.value,
      pillarExpertise: pillarExpertiseProps.selectedItems?.[0]?.value,
      contentGroupType: contentGroupTypeProps.selectedItems?.[0]?.value,
    });
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
  };

  useEffect(() => {
    const defaultContentGroupType = 'collection';
    setContentGroupTypeProps([defaultContentGroupType]);
  }, []);

  useEffect(() => {
    const _contentGroupType = contentGroupTypeProps.selectedItems?.[0]?.value;
    if (!!_contentGroupType && _contentGroupType?.length > 0) {
      setContentGroupType(_contentGroupType);
      refreshData().then();
    } else {
      setContentGroupType(undefined);
    }
  }, [
    pillarTypeProps.selectedItems,
    pillarExpertiseProps.selectedItems,
    categoryProps.selectedItems,
    contentGroupTypeProps.selectedItems,
  ]);

  const onEditContentGroup = (id: string) => {
    const foundContentGroup = data?.find((item) => item?.id === id);
    if (foundContentGroup) {
      navigate(`/content-groups/manage?id=${id}`);
    }
  };

  const onAddContentGroup = () => {
    navigate(`/content-groups/manage?content-group-type=${contentGroupType}`);
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);

  return (
    <ProjectContainer title={'Content Groups'}>
      <Components.Container>
        <Components.HorizontalHeaderWrapper>
          <Components.ContentGroupTypeWrapper>
            <Components.FieldLabel>Content Group Type</Components.FieldLabel>
            <ProjectPicker dropdownProps={contentGroupTypeProps} notSetText={'Select an option'} />
            <ProjectSearchDropdown {...contentGroupTypeProps} />
          </Components.ContentGroupTypeWrapper>
          {!!contentGroupType && contentGroupType?.length > 0 && (
            <ProjectTableHeader
              onSearchChange={searchProps.onChangeText}
              searchLabel={`Search by ${contentGroupType} title or ID`}
              searchPlaceholder={'Enter search term'}>
              <Components.SearchBarChildrenWrapper>
                <Components.SearchBarSeparator />
                <Components.FiltersWrapper>
                  <ProjectPicker dropdownProps={pillarTypeProps} notSetText={'Pillar type'} />
                  <ProjectSearchDropdown {...pillarTypeProps} />
                  <ProjectPicker dropdownProps={pillarExpertiseProps} notSetText={'Pillar expertise'} />
                  <ProjectSearchDropdown {...pillarExpertiseProps} />
                  <ProjectPicker dropdownProps={categoryProps} notSetText={'Category'} />
                  <ProjectSearchDropdown {...categoryProps} />
                </Components.FiltersWrapper>
                <Components.SearchBarSeparator />
                <Components.PrimaryButton onClick={onAddContentGroup}>
                  Add {getDisplayNames.contentGroupType(contentGroupType)}
                </Components.PrimaryButton>
              </Components.SearchBarChildrenWrapper>
            </ProjectTableHeader>
          )}
        </Components.HorizontalHeaderWrapper>

        {!!contentGroupType && contentGroupType?.length > 0 && (
          <Components.TableWrapper>
            {contentGroupType === ('collection' as ContentGroupType) ? (
              <ProjectTable
                data={data || []}
                columns={tableColumns.collections({ onEdit: onEditContentGroup })}
                filteringModel={_filteringModel}
                sortingModel={{ field: 'publishedAt', sort: 'desc' }}
                showRowCountText={'Found collections'}
              />
            ) : contentGroupType === ('bundle' as ContentGroupType) ? (
              <ProjectTable
                data={data || []}
                columns={tableColumns.bundles({ onEdit: onEditContentGroup })}
                filteringModel={_filteringModel}
                sortingModel={{ field: 'publishedAt', sort: 'desc' }}
                showRowCountText={'Found bundles'}
              />
            ) : (
              <></>
            )}
          </Components.TableWrapper>
        )}
      </Components.Container>
    </ProjectContainer>
  );
};

export default ContentGroups;
